import React from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Box,
  Text,
  Button,
  Link,
  Table,
  TableHeader,
  TableHeaderRow,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Amount,
} from '@razorpay/blade/components';
import officeLocationIcon from 'assets/officeLocation/office-location-icon.png';
import { getStaticMediaUrl } from 'utils/Urls';
import { ResetLocalOverridingStyles } from 'components/RunPayroll/ComplianceActions/styles';
import {
  getHasOverageFees,
  getHasRenewalFees,
  getRenewalPeriod,
  getRenewalPeriodText,
  getUpgradePricingPlanEmailTemplate,
  renewalPeriods,
} from './utils';
import { useRunPayrollDataContext } from 'components/RunPayroll/contexts/RunPayrollDataContext';
import useReduxSelector from 'utils/useReduxSelector';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';

export const RazorpayChargesBreakdownV2 = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const orgName = useReduxSelector(loggedInUserSelectors.organizationBrandName);
  const { name: userName } = useReduxSelector(loggedInUserSelectors.employeeDetails) || {};
  const payrollData = useRunPayrollDataContext();
  const { requiredAmount, flexiPartnerName } = payrollData || {};

  const { pricingDetails } = requiredAmount?.['opfin-charges'] ?? {};

  const {
    flexiPlatformChargesWithoutGst,
    flexiPartnerPlatformChargesGst,
    peopleOrgIdsForFlexiPartnerPlatformCharges,
    flexiPartnerCardChargesWithoutGst,
    flexiPartnerCardChargesGst,
    peopleOrgIdsForFlexiPartnerCardCharges,
    gst,
    total,
  } = requiredAmount?.['opfin-charges'] ?? {};

  const flexiPlatformCharges =
    (flexiPlatformChargesWithoutGst ?? 0) + (flexiPartnerPlatformChargesGst ?? 0);
  const flexiCardCharges =
    (flexiPartnerCardChargesWithoutGst ?? 0) + (flexiPartnerCardChargesGst ?? 0);
  if (!pricingDetails) {
    return null;
  }

  const hasOverageFees = getHasOverageFees(pricingDetails);
  const hasRenewalFees = getHasRenewalFees(pricingDetails);
  const renewalPeriod = getRenewalPeriod(pricingDetails.renewal_period);
  const renewalPeriodText = getRenewalPeriodText(pricingDetails.renewal_period);

  const getTableData = () => {
    const tableData = [];

    if (hasRenewalFees) {
      tableData.push({
        id: 'base_plan',
        description: renewalPeriod === renewalPeriods.MONTHLY ? 'Base Plan Fee' : 'Renewal Fee',
        calculation: `${renewalPeriodText.plan} (valid up to ${pricingDetails.employee_limit} employees)`,
        amount: pricingDetails.base_charges ?? 0,
      });
    }

    if (hasOverageFees) {
      tableData.push({
        id: 'overage',
        description: 'Beyond Plan Limit',
        calculation: `${pricingDetails.overage_employees} x ₹${pricingDetails.overage_rate}/employee`,
        amount: pricingDetails.overage_charges ?? 0,
      });
    }

    if (flexiPlatformCharges > 0) {
      tableData.push({
        id: 'flexi_platform_charges',
        description: `${flexiPartnerName} platform charges (${
          peopleOrgIdsForFlexiPartnerPlatformCharges?.length ?? 0
        } employees)`,
        calculation: '',
        amount: flexiPlatformChargesWithoutGst ?? 0,
      });
    }
    if (flexiCardCharges > 0) {
      tableData.push({
        id: 'flexi_card_charges',
        description: `${flexiPartnerName} card charges (${
          peopleOrgIdsForFlexiPartnerCardCharges?.length ?? 0
        } employees)`,
        calculation: '',
        amount: flexiPartnerCardChargesWithoutGst ?? 0,
      });
    }
    if (gst && gst > 0) {
      tableData.push({
        id: 'gst',
        description: 'GST Charges',
        calculation: '',
        amount: gst ?? 0,
      });
    }
    tableData.push({
      id: 'total',
      description: 'Total Platform Fees',
      calculation: '',
      amount: (total ?? 0) + (gst ?? 0),
    });
    return tableData;
  };

  const tableData = getTableData();

  const title =
    hasRenewalFees && renewalPeriod !== renewalPeriods.MONTHLY
      ? 'Your Plan is Renewing!'
      : hasOverageFees
      ? 'Your company has grown!'
      : '';

  const subtitle =
    hasRenewalFees && renewalPeriod !== renewalPeriods.MONTHLY
      ? 'Effective from this payroll month'
      : hasOverageFees
      ? `Your payroll is now over your ${renewalPeriodText.plan?.toLowerCase()} plan's
  limit for [${pricingDetails.employee_limit} Employees] . An overage fee is
  added for each extra employee`
      : '';

  return (
    <Modal isOpen={isOpen} onDismiss={onClose} size="medium" accessibilityLabel="Razorpay Charges">
      <ModalHeader title="Razorpay Charges" />
      <ModalBody>
        <ResetLocalOverridingStyles>
          <Box marginBottom="spacing.7">
            {title ? (
              <Box display="flex" alignItems="center" marginBottom="spacing.7">
                <img
                  src={getStaticMediaUrl(officeLocationIcon)}
                  alt=""
                  width="42px"
                  height="auto"
                />
                <Box marginLeft="spacing.3">
                  <Text size="large" weight="semibold" color="surface.text.gray.normal">
                    {title}
                  </Text>
                  {subtitle ? (
                    <Text size="small" weight="regular" color="surface.text.gray.muted">
                      {subtitle}
                    </Text>
                  ) : null}
                </Box>
              </Box>
            ) : null}

            <Table
              gridTemplateColumns="2fr 2fr 1fr"
              showStripedRows={false}
              data={{
                nodes: tableData,
              }}>
              {(tableData) => (
                <>
                  <TableHeader>
                    <TableHeaderRow>
                      <TableHeaderCell>Description</TableHeaderCell>
                      <TableHeaderCell>Calculation</TableHeaderCell>
                      <TableHeaderCell textAlign="right">Amount</TableHeaderCell>
                    </TableHeaderRow>
                  </TableHeader>

                  <TableBody>
                    {tableData.map((item) => (
                      <TableRow key={item.id} item={item}>
                        <TableCell>
                          <Text weight={item.id === 'total' ? 'semibold' : 'regular'}>
                            {item.description}
                          </Text>
                        </TableCell>
                        <TableCell>
                          <Text weight={item.id === 'total' ? 'semibold' : 'regular'}>
                            {item.calculation}
                          </Text>
                        </TableCell>
                        <TableCell textAlign="right">
                          <Amount
                            weight={item.id === 'total' ? 'semibold' : 'regular'}
                            value={item.amount}
                            suffix="decimals"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </>
              )}
            </Table>
          </Box>
          <Text size="medium" weight="regular" color="surface.text.gray.muted">
            Need help with a bigger plan?{' '}
            <Link
              href={getUpgradePricingPlanEmailTemplate({
                orgName: orgName || '',
                userName: userName || '',
              })}>
              Upgrade your plan
            </Link>{' '}
            to get more employees included in your {renewalPeriodText.plan?.toLowerCase()} fee.
          </Text>
        </ResetLocalOverridingStyles>
      </ModalBody>
      <ModalFooter>
        <Box display="flex" justifyContent="flex-end">
          <Button onClick={onClose}>Close</Button>
        </Box>
      </ModalFooter>
    </Modal>
  );
};
